<template>
  <!-- <el-dialog
    v-model="dialogVisible"
    title="Upload Document"
    width="600px"
    :close-on-click-modal="false"
    class="upload-document-modal"
  > -->
  <Dialog
    v-model="dialogVisible"
    title="Upload Document"
    dialog-type="primary"
    dialog-size="small"
    title-variant="medium"
  >
    <template #header-icon>
      <img src="@/assets/MyPatient/document_white.svg" alt="Document" class="w-6 h-6" />
    </template>
    <!-- <template #header>
      <div class="flex justify-between items-center px-6 py-4 bg-[#004879] text-white rounded-t-[10px]">
        <div class="flex items-center gap-1">
          <img src="@/assets/MyPatient/document_white.svg" alt="Document" class="w-6 h-6" />
          <span class="font-semibold text-[20px] leading-normal text-white">Upload Document</span>
        </div>
        <button class="bg-transparent border-none p-0 cursor-pointer w-7 h-7" @click="closeDialog">
          <img src="@/assets/MyPatient/close.svg" alt="Close" class="w-full h-full" />
        </button>
      </div>
    </template> -->
    <div class="modal-content">
      <!-- Upload Files Section -->
      <div class="bg-[#F9FBFD] rounded-lg">
        <h3 class="font-semibold text-base text-[#333333] m-0 mb-2">Upload files</h3>
        <p class="font-normal text-sm text-[#666666] m-0 mb-4">Max file size is 30MB.</p>
        
        <!-- Uploaded Files List -->
        <div v-if="uploadedFiles.length > 0" class="uploaded-files mb-4">
          <div 
            v-for="(file, index) in uploadedFiles" 
            :key="index"
            class="uploaded-file h-auto flex items-center justify-between px-4 py-3 mb-2 rounded relative overflow-hidden"
            :class="{
              'bg-[#f6ffed]': file.status === 'uploaded',
              'bg-[#fff2f0]': file.status === 'failed',
              'bg-[#f0f9ff]': file.status === 'uploading'
            }"
          >
            <!-- Progress bar background for uploading files -->
            <div 
              v-if="file.status === 'uploading'"
              class="absolute top-0 left-0 h-full bg-gradient-to-r from-[#e6f7ff] to-[#bae7ff] transition-all duration-300 ease-out"
              :style="{ width: (uploadProgress[file.name] || 0) + '%' }"
            ></div>
            
            <div class="document-info flex flex-col flex-1 relative z-10">
              <div class="file-name font-['Inter'] font-normal text-[14px] leading-[20px] tracking-[0.25px] overflow-ellipsis overflow-hidden whitespace-nowrap"
                   :class="{
                     'text-[#000000]': file.status === 'uploaded',
                     'text-[#ff4d4f]': file.status === 'failed',
                     'text-[#0050b3]': file.status === 'uploading'
                   }">
                {{ file.name }}
                <span v-if="file.status === 'failed'" class="text-[#ff4d4f] ml-2">(Upload Failed)</span>
                <span v-else-if="file.status === 'uploaded'" class="text-[#52c41a] ml-2">✓</span>
                <span v-else-if="file.status === 'uploading'" class="text-[#0050b3] ml-2">({{ uploadProgress[file.name] || 0 }}%)</span>
              </div>
              <div class="file-details font-['IBM_Plex_Sans_Hebrew'] text-[12px] leading-[16px] tracking-[0.4px]"
                   :class="{
                     'text-[#979797]': file.status === 'uploaded',
                     'text-[#ff4d4f]': file.status === 'failed',
                     'text-[#0050b3]': file.status === 'uploading'
                   }">
                {{ getFileType(file.originalFile || file) }} • {{ formatFileSize((file.originalFile || file).size) }} • {{ getFileExtension(file.name) }}
                <span v-if="file.status === 'failed' && file.error" class="block mt-1 text-[#ff4d4f]">{{ file.error }}</span>
              </div>
            </div>
            
            <div class="flex items-center gap-2 relative z-10">
              <!-- Retry button for failed uploads -->
              <button 
                v-if="file.status === 'failed'" 
                class="px-3 py-1 text-[12px] bg-[#1890ff] text-white rounded hover:bg-[#40a9ff] transition-colors"
                :disabled="isUploading"
                @click="retryUpload(file, index)"
              >
                Retry
              </button>
              <!-- Remove button -->
              <button 
                class="remove-file-btn p-1 rounded hover:bg-[#e6e6e6] transition-colors"
                @click="removeFile(index)"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="#999" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Upload Area -->
        <div 
          class="upload-area flex items-center justify-center"
          @drop.prevent="handleDrop"
          @dragover.prevent
          @dragenter.prevent
          @dragleave.prevent
        > 
          <div class="flex flex-col justify-center max-content">
            <div class="flex justify-center"> 
              <img src="@/assets/MyPatient/Add_File.svg" alt="Upload" class="upload-icon" />
            </div>
            <div class="upload-text mb-2">Drag and drop your files here to add,</div>
            <div class="upload-or mb-2">OR</div>
            <div class="flex justify-center">
              <button 
                class="bg-[#1e4670] fit-content text-white px-8 py-1 rounded-[15px] hover:bg-[#2a5490] transition-colors" 
                @click="selectFiles"
              >
                Add File
              </button>
            </div>
            
            <input
              ref="fileInput"
              type="file"
              multiple
              :accept="getAllowedFileTypes(allowedMIMETypes)"
              style="display: none"
              @change="handleFileSelect"
            /> 
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <!-- <div class="px-6"> 
        <div class="flex footer-dasher justify-between gap-2.5 py-6 bg-[#F9FBFD]">
          <Button color-type="red-stroke" @click="closeDialog">Cancel</Button>
          <Button 
            color-type="dark-blue" 
            :disabled="uploadedFiles.length === 0 || isUploading"
            @click="confirmUpload"
          >
            <span class="text-[14px] leading-5 tracking-[0.1px]" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Upload</span>
          </Button>
        </div>
      </div> -->
      <ResponsiveButton
        display_name = "Cancel"
        size="medium"
        built_in_theme = "error"
        button_type="outlined"
        @click-button="closeDialog"
      />
      <ResponsiveButton
        display_name = "Upload"
        size="medium"
        built_in_theme = "dark"
        button_type="filled"
        :disabled="uploadedFiles.length === 0 || isUploading"
        @click-button="confirmUpload"
      />
    </template>
  </Dialog>
  <!-- </el-dialog> -->
</template>

<script setup>
import { ref, computed } from 'vue';
import Button from '@/NewLibrary/Button.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  allowedMIMETypes: {
    type: Array,
  }
});

const emit = defineEmits(['close', 'upload-success']);

const mimeToExtMap = {
  // 📄 文档类
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],

  // 🖼️ 图片
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],

  // 🎵 音频
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/ogg': ['.ogg'],

  // 🎬 视频
  'video/mp4': ['.mp4'],
  'video/x-msvideo': ['.avi'],
  'video/x-matroska': ['.mkv'],
  'video/webm': ['.webm'],

  // 📦 压缩包
  'application/zip': ['.zip'],
  'application/x-rar-compressed': ['.rar'],
  'application/x-7z-compressed': ['.7z'],
  'application/gzip': ['.gz'],
  'application/x-tar': ['.tar'],

  // 💻 代码/配置
  'application/json': ['.json'],
  'text/html': ['.html', '.htm'],
  'text/css': ['.css'],
  'application/javascript': ['.js'],
  'text/javascript': ['.js'], // 兼容
  'application/xml': ['.xml'],
}

const uploadedFiles = ref([]);
const uploadingFiles = new Set();
const isUploading = ref(false);
const uploadProgress = ref({});
const fileInput = ref(null);

const dialogVisible = computed({
  get() {
    return props.visible;
  },
  set(value) {
    if (!value) {
      emit('close');
    }
  }
});

function selectFiles() {
  fileInput.value.click();
}

function handleFileSelect(event) {
  const files = Array.from(event.target.files);
  addFiles(files);
}

function handleDrop(event) {
  const files = Array.from(event.dataTransfer.files);
  addFiles(files);
}

function getAllowedFileTypes(allowedMIMETypes) {
  if (!allowedMIMETypes || allowedMIMETypes.length === 0) {
    return null;
  }
  
  // 将MIME类型转换为文件扩展名
  const extensions = allowedMIMETypes.flatMap(mime => mimeToExtMap[mime] || []);
  return extensions.join(',');
}

async function addFiles(files) {
  isUploading.value = true;
  
  const validFiles = [];
  for (const file of files) {
    if (isValidFile(file)) {
      validFiles.push(file);
    } else {
      console.error(`File "${file.name}" is not valid. Max size: 30MB.`);
    }
  }
  
  // Show dummy upload animation for UX
  const uploadPromises = validFiles.map(file => {
    return simulateUpload(file).catch(error => {
      console.error(`Simulated upload failed for ${file.name}:`, error);
      return { error: true, file: file.name, message: error.message };
    });
  });
  
  await Promise.all(uploadPromises);
  
  isUploading.value = false;
}

async function simulateUpload(file) {
  const uploadKey = Date.now() + '-' + file.name;
  uploadingFiles.add(uploadKey);
  
  uploadProgress.value[file.name] = 0;
  
  const uploadingFile = {
    originalFile: file,
    name: file.name,
    size: file.size,
    type: file.type,
    uploadedAt: new Date(),
    status: 'uploading'
  };
  uploadedFiles.value.push(uploadingFile);
  
  const progressInterval = setInterval(() => {
    const currentProgress = uploadProgress.value[file.name] || 0;
    if (currentProgress < 90) {
      const increment = Math.random() * 15 + 5;
      const newProgress = Math.min(currentProgress + increment, 90);
      uploadProgress.value[file.name] = Math.round(newProgress);
    }
  }, 300);
  
  try {
    // Dummy upload animation - actual upload happens on confirmUpload
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    clearInterval(progressInterval);
    uploadProgress.value[file.name] = 100;
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const fileIndex = uploadedFiles.value.findIndex(f => f.name === file.name && f.status === 'uploading');
    if (fileIndex !== -1) {
      uploadedFiles.value[fileIndex] = {
        originalFile: file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date(),
        status: 'uploaded'
      };
    }
    
    delete uploadProgress.value[file.name];
    
  } catch (error) {
    clearInterval(progressInterval);
    
    const fileIndex = uploadedFiles.value.findIndex(f => f.name === file.name && f.status === 'uploading');
    if (fileIndex !== -1) {
      uploadedFiles.value[fileIndex] = {
        originalFile: file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date(),
        status: 'failed',
        error: error.message
      };
    }
    
    delete uploadProgress.value[file.name];
    throw error;
    
  } finally {
    uploadingFiles.delete(uploadKey);
  }
}

async function retryUpload(failedFile, index) {
  if (!failedFile.originalFile) {
    return;
  }
  
  uploadedFiles.value.splice(index, 1);
  await simulateUpload(failedFile.originalFile);
}

function isValidFile(file) {
  const maxSize = 30 * 1024 * 1024; // 30MB
  
  return file.size <= maxSize && (!props.allowedMIMETypes || props.allowedMIMETypes.length === 0 || props.allowedMIMETypes.includes(file.type));
}

function removeFile(index) {
  uploadedFiles.value.splice(index, 1);
}

function closeDialog() {
  resetForm();
  emit('close');
}

function confirmUpload() {
  const successfulFiles = uploadedFiles.value.filter(f => f.status === 'uploaded');
  // Emit files with their original File objects for actual upload
  const filesToUpload = successfulFiles.map(f => ({
    file: f.originalFile,
    name: f.name,
    size: f.size,
    type: f.type
  }));
  emit('upload-success', filesToUpload);
  resetForm();
  emit('close');
}

function resetForm() {
  uploadedFiles.value = [];
  uploadingFiles.clear();
  isUploading.value = false;
  uploadProgress.value = {};
  
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function getFileType(file) {
  const fileType = file.type || '';
  
  if (fileType.startsWith('image/')) return 'Image';
  if (fileType === 'application/pdf') return 'PDF';
  if (fileType === 'application/msword' || 
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return 'Document';
  }
  
  return 'File';
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileExtension(filename) {
  return filename.split('.').pop().toLowerCase();
}
</script>

<style scoped>
.modal-content {
  padding: 24px;
}

.upload-area {
  border: 2px dashed var(--DividerGray, #C4C4C4);
  border-radius: 8px;
  padding: 40px 20px;
  background: var(--Gray-White, #F2F2F2);
  text-align: center;
  transition: border-color 0.2s ease;
}

.upload-area:hover {
  border-color: #1890ff;
}

.upload-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.upload-text,
.upload-or {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #979797;
}

.remove-file-btn {
  background: transparent;
  border: none;
  cursor: pointer;
}

.footer-dasher {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
}
</style>

<style>
.upload-document-modal .el-overlay-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px;
}

.upload-document-modal.el-dialog {
  border-radius: 10px;
  --el-dialog-padding-primary: 0px !important;
}

/* Max height dialog styling */
.upload-document-modal .el-dialog {
  max-height: 80vh !important;
  display: flex;
  flex-direction: column;
}
.upload-document-modal .el-dialog__headerbtn {
  display: none;
}

.upload-document-modal .el-dialog__body {
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #F9FBFD;
}

/* Scrollable content container */
.dialog-scrollable-content {
  max-height: calc(80vh - 120px); /* Subtract header and footer height */
  overflow-y: auto;
  flex: 1;
}
.upload-document-modal .el-dialog__header {
  padding: 0;
  margin: 0;
}

.upload-document-modal .el-dialog__body {
  padding: 0;
}

.upload-document-modal .el-dialog__footer {
  padding: 0;
}

.upload-document-modal {
  border-radius: 12px;
  overflow: hidden;
}
</style>
